import axiosInstance from "../../../setupAxios";
import { IWorkflow } from "../../types/IWorkflow";
import { IUsage } from "../../types/usageTypes/IUsage";
import { IUsageGroupLocation } from "../../types/usageTypes/IUsageGroupLocation";
import { IUsageGroupSetListAdditional } from "../../types/usageTypes/IUsageGroupSetListAdditional";
import { IUsageMatch } from "../../types/usageTypes/IUsageMatch";
import { IUsageGroupVenueDetails } from "../../types/usageTypes/IUsageGroupVenueDetails";
import { IUsageGroup } from "../../types/usageTypes/IUsageGroup";
import { IDistributionState } from "../../../redux/types/IDistributionState";
import { IUsagePoolState } from "../../../redux/types/IUsagePoolState";
import { ILicenseUsage } from "../../types/usageTypes/ILicenseUsage";
import { IArtist } from "../../types/IArtist";
import { ICustomerVenue } from "../../types/ISetListVenue";
import { IApplyMatchToMultipleRecordsRequest } from "../../types/usageTypes/IApplyMatchToMultipleRecordsRequest";
import { IApplyMatchToMultipleRecordsResponse } from "../../types/usageTypes/IApplyMatchToMultipleRecordsResponse";
import { ISplitLicenseResult } from "../../types/usageTypes/ISplitLicenseResult";
import { IBulkUsageIpAdjustmentRequest } from "../../types/usageTypes/IBulkUsageIpAdjustmentRequest";
import { IBulkUsageIpAdjustmentResponse } from "../../types/usageTypes/IBulkUsageIpAdjustmentResponse";

export class UsageService {

    public static splitLicense(request: {
        sourceUsageGroupId: string;
        targetUsageGroupId: string;
        sourceLicensingSystemId: string;
        newLicensingSystemId: string;
        originalLicenseFee: number;
        newLicenseFee: number;
    }): Promise<ISplitLicenseResult> {
        return new Promise<ISplitLicenseResult>((resolve, reject) => {
            axiosInstance
            .post<ISplitLicenseResult>("usage/SplitLicense", request)
            .then(response => {
                resolve(response.data);
            })
            .catch (err => {
                reject(err.response.status);
            });
        });
    }
    

    private static buildCreationMetadataPayload(creationMetadata?: {
        fullTitle?: string;
        fullContributor?: string;
        fullPerformer?: string;
    }): Record<string, string | null> {
        if (!creationMetadata) {
            return {};
        }

        return {
            fullTitle: creationMetadata.fullTitle || null,
            title: creationMetadata.fullTitle || null,
            fullContributor: creationMetadata.fullContributor || null,
            fullPerformer: creationMetadata.fullPerformer || null
        };
    }
    
    public static getUsage(usageID: string): Promise<IUsageGroup> {
        return new Promise<IUsageGroup>((resolve, reject) => {
            axiosInstance
                .get("/usage/id/" + usageID)
                .then(response => {
                    if (response.status === 200) {
                        const result = response.data;
                        resolve(result);
                    }
                    else {
                        throw response;
                    }
                })
                .catch(err => {
                    reject(err.response.status);
                });
        });
    }

    public static getDistributions(): Promise<IDistributionState[]> {
        return new Promise<IDistributionState[]>((resolve, reject) => {
            axiosInstance.get("Distribution/Distributions/")
                .then(res => {
                    resolve(res.data);
                })
                .catch(reject);
        });
    }

    public static getPools(): Promise<IUsagePoolState[]> {
        return new Promise<IUsagePoolState[]>((resolve, reject) => {
            axiosInstance.get("usagePool/Pools/")
                .then(res => {
                    resolve(res.data);
                })
        });
    }
    public static getSources(matchType: string, level: number): Promise<string[]> {
        return new Promise<string[]>((resolve, reject) => {
            axiosInstance.get("Sources/GetSourcesByMatchType/" + matchType + "/" + level)
                .then(res => {
                    resolve(res.data);
                })
        });
    }

    static saveUsageGroup(
        matchingWorks: IUsageMatch[], 
        workflows: IWorkflow[], 
        id: string, 
        matchStatus: string,
        poolCode: string,
        usages?: IUsage[],
        location?: IUsageGroupLocation,
        setListAdditional?: IUsageGroupSetListAdditional,
        approvalStatus?: string,
        venueDetails?: IUsageGroupVenueDetails,
        isFromSearchResults?: boolean,
        qaStatus?: string,
        comments?: any,
        licenses?: ILicenseUsage[],
        setListArtist?: IArtist[],
        customerVenues?: ICustomerVenue[],
        catOne?: string,
        catTwo?: string,
        createMatchHistory?: boolean,
        usagePool?: IUsagePoolState,
        creationMetadata?: {
            fullTitle?: string;
            fullContributor?: string;
            fullPerformer?: string;
        }
    ): Promise<any> {
        const url: string = "usage/save/" + id;
        const creationPayload = UsageService.buildCreationMetadataPayload(creationMetadata);
        let usageRowData: IUsage[] = [];
        if(usages === undefined || usages === null) {
            usages = usageRowData;
        }
        const data = {
            usageMatches: matchingWorks,
            workflows: workflows,
            matchStatus: matchStatus,
            ...creationPayload,
            usages: usages,
            location: { ... location },
            setListAdditional: {...setListAdditional },
            approvalStatus,
            venueDetails: { ...venueDetails },
            isFromSearchResults: isFromSearchResults,
            qaStatus: qaStatus || null,
            comments: comments || null,
            licenses: licenses || null,
            setListArtist: setListArtist || null,
            customerVenues: customerVenues || null,
            catOne: catOne || null,
            catTwo: catTwo || null,
            createMatchHistory: createMatchHistory,
            poolCode: poolCode || null,
            usagePool: usagePool ? { ...usagePool } : null
        };
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post(url, data)
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                });
        });
    }

    static createNewUsageGroup(usageGroup: IUsageGroup): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post("usage/CreateNewUsageGroup", usageGroup)
                .then(resolve)
                .catch(err => {
                    console.error(err);
                    reject(err);
                });
        });
    }

    static async mergeUsage(usageGroup: IUsageGroup, targetUsageGroupID: number, mergeOption: string): Promise<any> {
        const url: string = "usage/merge/" + targetUsageGroupID + '/' + mergeOption;
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .patch(url, usageGroup)
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                });
        });
    }

    static async updateUsageApprovalStatus(
        usageGroupId: string,
        approved: boolean
    ): Promise<any> {
        
        const data = {
            approved: approved
        }

        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .put(`usage/approval/${usageGroupId}`, data)
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                });
        });
    }

    static undoUsageIngestion(fullFilePath: string): Promise<any> {
        const url: string = encodeURI("storage/undo/" + fullFilePath);
        return new Promise<any>((resolve, reject) => {
            axiosInstance.delete(url).then(res => resolve(res)).catch(err => reject(err));
        });
    }

    public static getPaymentRunIDs(): Promise<string[]> {
        return new Promise<string[]>((resolve, reject) => {
            axiosInstance.get("usage/getPaymentRunIDs")
                .then(res => {
                    resolve(res.data);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                });
        });
    }
    static saveRegroupedUsage(regroupedUsage: IUsageGroup,id?:string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post(`usage/saveRegroupedUsageGroup/${id}`, regroupedUsage)
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                })
        })
    }

    static async applyMatchToMultipleRecords(request: IApplyMatchToMultipleRecordsRequest): Promise<IApplyMatchToMultipleRecordsResponse> {
        const response =
            await axiosInstance.post<IApplyMatchToMultipleRecordsResponse>(
                "/Usage/bulk-match",
                request
            );

        if (response.status !== 202) {
            throw response;
        }

        return response.data;
    }

    static async submitBulkUsageIpAdjustment(request: IBulkUsageIpAdjustmentRequest): Promise<IBulkUsageIpAdjustmentResponse> {
        const response = await axiosInstance.post<IBulkUsageIpAdjustmentResponse>(
            "/Usage/bulk-ip-adjustment",
            request
        );

        if (response.status !== 202) {
            throw response;
        }

        return response.data;
    }
}

